import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';

// Single flag controlling whether this noindex, unlinked knowledge-base page
// is allowed to be indexed. Flip to `true` (and mirror it in
// prerenderRoutes.ts for this route) when the page is ready to go public —
// that is the only change needed.
const INDEXABLE = false;

// Originally kept at a flat /guides/... slug because /en/ and /de/ paths
// were routed straight to the landingpages Cloud Run service by
// firebase.json, not main-site. That's no longer true — the DE/EN URL
// migration narrowed firebase.json's /en/** rewrite to the landing pages'
// own slugs and made /en/... a first-class main-site prefix (see
// firebase.json and App.tsx's route table).
const PATH = '/en/guides/starting-out-self-employed/';
const CANONICAL = `https://pstax.at${PATH}`;
const TITLE = 'Starting Out as Self-Employed in Austria: The Full Path (2026) | PSTax';
const DESCRIPTION =
  'Which registrations you need, in which order, what your first year really costs — and the one question to settle before any of it. A plain-English guide for freelancers in Austria.';

const SVS_GUIDE_PATH = '/en/guides/svs-social-insurance/';
const YEAR_END_PATH = '/en/guides/svs-social-insurance/short/';
// No income-tax-for-the-self-employed guide exists yet anywhere in this
// codebase (checked main-site/src/pages, App.tsx's route list and
// prerenderRoutes.ts). Per the content brief's own instruction for a missing
// target page, every reference to it below renders as a "Soon" placeholder
// instead of a dead link.

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'May you work here at all?', id: 'permit' },
  { label: 'Which kind of self-employed are you?', id: 'which-route' },
  { label: 'The registrations, in order', id: 'steps' },
  { label: 'The discount most founders miss', id: 'neufoeg' },
  { label: 'What your first year actually costs', id: 'first-year' },
  { label: 'Do you need a tax advisor?', id: 'advisor' },
  { label: 'Frequently asked questions', id: 'faq' },
  { label: 'Official sources', id: 'sources' },
];

const AT_A_GLANCE: [string, string][] = [
  ['Settle this first', 'May you be self-employed here?'],
  ['Two routes', 'Trade licence, or Neue Selbständige'],
  ['Who you register with', 'District authority, Finanzamt, SVS'],
  ['Deadline', 'Tell the Finanzamt within one month'],
  ['VAT-free up to', '€55,000 turnover'],
  ['Put aside', 'About a third of your profit'],
];

const STEPS: { title: string; body: React.ReactNode }[] = [
  {
    title: 'Clarify your route',
    body: (
      <>
        Trade licence or <span lang="de">Neue:r Selbständige:r</span>, free trade or regulated. The Economic
        Chamber's founder service will confirm it for you and costs nothing.
      </>
    ),
  },
  {
    title: "Register the trade — or don't",
    body: (
      <>
        With a licence: at the district authority (<span lang="de">Bezirkshauptmannschaft</span>) or city
        magistrate, or online through the business portal. Registration itself is free. Without a licence: nothing
        to register here.
      </>
    ),
  },
  {
    title: 'Tell the Finanzamt',
    body: (
      <>
        Within one month of starting. You complete a questionnaire about your expected income and receive your tax
        number. This is separate from every other registration — nobody does it for you.
      </>
    ),
  },
  {
    title: 'Sort out the SVS',
    body: (
      <>
        With a trade licence this happens automatically once you register. As a{' '}
        <span lang="de">Neue:r Selbständige:r</span> you register yourself, within one month of starting.
      </>
    ),
  },
  {
    title: 'Decide on VAT',
    body: (
      <>
        Below €55,000 turnover in 2026 you can stay VAT-exempt as a small business: no VAT on your invoices, but no
        input VAT back either. Above it, or if you opt in voluntarily, you need a VAT number and file returns.
      </>
    ),
  },
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating all four answers as a second copy of the text.
export const FAQS_STARTING_OUT: { q: string; a: React.ReactNode; aPlain: string }[] = [
  {
    q: 'Can I start before all the registrations are done?',
    aPlain:
      'With a free trade you may generally begin as soon as the registration is filed. But "generally" is doing work in that sentence — regulated trades can require a decision first, and the Finanzamt and SVS deadlines run from the day you actually start, not the day you get around to the paperwork. Register first, invoice second.',
    a: (
      <>
        With a free trade you may generally begin as soon as the registration is filed. But "generally" is doing
        work in that sentence — regulated trades can require a decision first, and the Finanzamt and SVS deadlines
        run from the day you actually start, not the day you get around to the paperwork. Register first, invoice
        second.
      </>
    ),
  },
  {
    q: 'What does it cost to register?',
    aPlain:
      'The trade registration itself is free. What costs money from day one is the SVS — €160.81 a month in 2026 with a trade licence, regardless of revenue. Budget for that before you budget for anything else.',
    a: (
      <>
        The trade registration itself is free. What costs money from day one is the SVS — €160.81 a month in 2026
        with a trade licence, regardless of revenue. Budget for that before you budget for anything else.
      </>
    ),
  },
  {
    q: 'Do I need a business bank account?',
    aPlain:
      'Not legally, as a sole trader. In practice it makes bookkeeping enormously easier and it is the difference between an evening and a weekend at year-end. A separate account for the money you set aside for tax and SVS is worth even more.',
    a: (
      <>
        Not legally, as a sole trader. In practice it makes bookkeeping enormously easier and it is the difference
        between an evening and a weekend at year-end. A separate account for the money you set aside for tax and
        SVS is worth even more.
      </>
    ),
  },
  {
    q: 'Do I have to speak German for any of this?',
    aPlain:
      'To do it yourself, realistically yes — the forms, the portals and every letter are in German. Working with an advisor, no. You forward what arrives and get an explanation in English.',
    a: (
      <>
        To do it yourself, realistically yes — the forms, the portals and every letter are in German. Working with
        an advisor, no. You forward what arrives and get an explanation in English.
      </>
    ),
  },
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener"
    className={`motion-reduce:transition-none ${className}`}
  >
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, opens in a new tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p className="text-[11px] font-black uppercase tracking-wider mb-2 text-pstax-navy">{label}</p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Soon
  </span>
);

// The one new component this page introduces (see the content brief) — a
// low-key partner referral, deliberately not styled like the CTA block so it
// reads as a pointer, not an ad.
const PartnerCard: React.FC = () => (
  <div className="my-10 rounded-2xl border-[1.5px] border-[#2f5f9e] bg-[#eef4fc] px-[26px] py-[28px]">
    <p className="text-[11px] font-black uppercase tracking-[0.11em] text-[#2f5f9e] mb-3">
      If you are starting from zero in Austria
    </p>
    <h3 className="text-xl font-display font-bold text-pstax-navy mb-3">Setup in Austria</h3>
    <p className="mb-4 leading-relaxed text-pstax-navy">
      For people who have just moved here and want the whole setup handled in one go, we run a dedicated service
      together with Miglena Hofer: business registration and tax setup in a single package, from the trade licence
      and SVS through to your tax office registration, VAT number and first year of filing.
    </p>
    <p className="mb-5 leading-relaxed text-pstax-navy">
      It exists because "which office first" is a genuinely bad first experience of a new country — and because
      doing it in the wrong order costs time you do not have while you are also finding a flat.
    </p>
    <a
      href="https://setupinaustria.at/"
      target="_blank"
      rel="noopener"
      className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full bg-[#2f5f9e] text-white font-semibold px-6 py-3 hover:bg-[#254c7e] transition-all active:scale-95 motion-reduce:transition-none"
    >
      Visit setupinaustria.at
      <span aria-hidden="true">↗</span>
      <span className="sr-only">, opens in a new tab</span>
    </a>
  </div>
);

export const StartingOutSelfEmployedGuide: React.FC = () => {
  useSeoHead({
    title: TITLE,
    description: DESCRIPTION,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'en',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/starting-out-self-employed/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/selbststaendig-werden/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/starting-out-self-employed/' },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Starting out as self-employed in Austria',
        datePublished: '2026-09-17',
        dateModified: '2026-09-17',
        inLanguage: 'en',
        author: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo' },
        publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo' },
        about: 'Becoming self-employed in Austria',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_STARTING_OUT.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="starting-out-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-sm text-pstax-gray mb-6">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/en/" className="hover:text-pstax-link">
                Home
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>Knowledge Base</li>
            <li aria-hidden="true">/</li>
            <li className="text-pstax-navy font-semibold" aria-current="page">
              Starting out
            </li>
          </ol>
        </nav>

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Knowledge Base · Getting started
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-6 leading-tight">
              Starting out as self-employed in Austria
            </h1>
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              Five registrations, in a particular order, with one question that has to be settled before any of
              them. Here is the whole path on one page — and where to read further once you are on it.
            </p>
            <p className="text-sm text-pstax-gray mb-4">Figures for 2026 · Last updated September 2026</p>
            <GuideLanguageSwitch from={PATH} targetLang="DE" label="Diese Seite auf Deutsch →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">Starting out at a glance</h2>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {AT_A_GLANCE.map(([label, value]) => (
                  <div
                    key={label}
                    className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm"
                  >
                    <dt className="text-pstax-gray">{label}</dt>
                    <dd className="font-semibold text-pstax-navy text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Calculator promo */}
            <div className="mb-10 bg-pstax-blue/10 border border-pstax-blue/30 rounded-2xl p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black uppercase tracking-wider text-pstax-navy mb-1">
                  Estimate your numbers
                </p>
                <p className="text-sm text-pstax-gray">
                  See roughly what income tax and SVS will take from your revenue in your first year.
                </p>
              </div>
              <Link
                to="/en/#calculator"
                className="btn-outline shrink-0 min-h-[44px] flex items-center justify-center whitespace-nowrap motion-reduce:transition-none"
              >
                Open the calculator
              </Link>
            </div>

            {/* Table of contents */}
            <nav className="toc-top mb-12" aria-label="On this page">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">On this page</p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item, i) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1 motion-reduce:transition-none"
                    >
                      <span className="font-bold text-pstax-blue mr-1">{i + 1}.</span> {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* May you work here at all? */}
            <h2 id="permit" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              May you work here at all?
            </h2>
            <p className="mb-4 leading-relaxed">
              Before any registration, one question has to be answered — and it is the one people skip, because it
              does not feel like a business question.
            </p>
            <p className="mb-4 leading-relaxed">
              If you are a citizen of the EU, the EEA or Switzerland, you may be self-employed in Austria without
              any permit. You can go straight to the next section.
            </p>
            <p className="mb-4 leading-relaxed">
              If you are a third-country national, you need a residence title that actually allows self-employment.
              There are several — the Red-White-Red Card for self-employed key workers, the Red-White-Red Card for
              start-up founders, and a residence permit for self-employed work. Which one fits depends on your
              plans, and the requirements are substantial.
            </p>

            <Callout tone="orange" label="The mistake that costs the most">
              A residence permit that allows you to take a job does not automatically allow you to be self-employed.
              These are different titles with different conditions. Check yours before you register a business, not
              after.
            </Callout>

            <p className="mb-8 leading-relaxed">
              This is immigration law, not tax law, and it is not what we do. The Austrian Business Agency
              maintains an English-language overview of the{' '}
              <ExternalLink
                href="https://www.workinaustria.com/en/residence-employment/residence-permits-for-self-employed/"
                className="text-pstax-link hover:underline"
              >
                residence permits for self-employed work
              </ExternalLink>
              , and that is the right place to start.
            </p>

            {/* Which kind of self-employed are you? */}
            <h2 id="which-route" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Which kind of self-employed are you?
            </h2>
            <p className="mb-4 leading-relaxed">
              Austria splits the self-employed into two groups, and almost everything downstream follows from which
              one you are in.
            </p>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>
                <strong>
                  With a trade licence (<span lang="de">Gewerbetreibende</span>)
                </strong>{' '}
                — IT services, photography, most consulting, personal training, the trades. You register a{' '}
                <span lang="de">Gewerbe</span>, become a member of the Economic Chamber, and your SVS insurance
                starts the same day.
              </li>
              <li>
                <strong>
                  Without a trade licence (<span lang="de">Neue Selbständige</span>)
                </strong>{' '}
                — writing, lecturing, translating, certain therapeutic and artistic work. No licence to register,
                but you have to register yourself with the SVS, and only once your profit passes €6,613.20 in 2026.
              </li>
            </ul>

            <Callout tone="blue" label="Important">
              This is not a choice. It follows from the activity itself, and getting it wrong is corrected
              retroactively. If your work sits near the line, have it checked before you register — the Economic
              Chamber's founder service does this free of charge.
            </Callout>

            <p className="mb-4 leading-relaxed">
              Trade licences also come in two kinds. Most are free trades, which you can register immediately. Some
              are regulated and require proof of qualification. Which is which is not always obvious from the job
              title.
            </p>
            <p className="mb-8 leading-relaxed">
              What each route means for your social insurance is covered in our{' '}
              <Link to={SVS_GUIDE_PATH} className="text-pstax-link hover:underline font-semibold">
                full SVS guide
              </Link>
              .
            </p>

            {/* The registrations, in order */}
            <h2 id="steps" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              The registrations, in order
            </h2>
            <p className="mb-6 leading-relaxed">
              The order matters — each step produces something the next one needs.
            </p>
            <ol className="mb-8 space-y-4">
              {STEPS.map((step, i) => (
                <li key={step.title} className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="leading-relaxed">
                    <strong>{step.title}</strong> — {step.body}
                  </p>
                </li>
              ))}
            </ol>

            {/* The discount most founders miss */}
            <h2 id="neufoeg" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              The discount most founders miss
            </h2>
            <p className="mb-4 leading-relaxed">
              Austria waives a set of fees and charges for genuinely new businesses under the{' '}
              <span lang="de">Neugründungs-Förderungsgesetz</span>, usually shortened to NeuFöG. It covers things
              like stamp duties and federal administrative fees on the founding paperwork, and — if you hire —
              certain employer contributions in the first twelve months.
            </p>
            <p className="mb-4 leading-relaxed">
              Two conditions matter: it has to be a genuinely new business structure, and you must not have run a
              comparable business in the previous five years.
            </p>

            <Callout tone="orange" label="Timing, not paperwork, is what people lose it on">
              The exemption is claimed with an official confirmation (form NeuFö2), and it has to be presented when
              you claim the exemption — not reconstructed afterwards. Get the confirmation before you file the
              registrations, not after.
            </Callout>

            {/* What your first year actually costs */}
            <h2 id="first-year" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What your first year actually costs
            </h2>
            <p className="mb-4 leading-relaxed">
              Two bills, from two authorities, on completely different schedules.
            </p>
            <p className="mb-4 leading-relaxed">
              The SVS starts immediately if you have a trade licence — €160.81 a month in 2026, even at zero
              revenue. That feels manageable, and that is the problem: in your first two years those contributions
              are only provisional, and the real amount is charged years later. Our{' '}
              <Link to={SVS_GUIDE_PATH} className="text-pstax-link hover:underline font-semibold">
                SVS guide
              </Link>{' '}
              explains how that works, and the{' '}
              <Link to={YEAR_END_PATH} className="text-pstax-link hover:underline font-semibold">
                year-end check
              </Link>{' '}
              explains what to do about it before it happens.
            </p>
            <p className="mb-4 leading-relaxed">
              The Finanzamt starts late. You pay no income tax in your first year at all. The bill for it arrives
              with your first assessment, typically two years on — together with quarterly prepayments for the year
              you are then in. See our guide to income tax for the self-employed.
              <SoonBadge />
            </p>
            <p className="mb-8 leading-relaxed">
              Which leads to the single most useful habit you can start on day one: put roughly a third of your
              profit aside, in a separate account, and do not touch it. In your first two years you will be setting
              aside more than you are being billed. That surplus is not spare money — it is the back-payment,
              waiting.
            </p>

            {/* Do you need a tax advisor? */}
            <h2 id="advisor" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Do you need a tax advisor?
            </h2>
            <p className="mb-4 leading-relaxed">
              Honestly: no, not legally. Plenty of self-employed people in Austria file their own returns and manage
              perfectly well, and if your situation is simple and your German is good, that is a reasonable choice.
            </p>
            <p className="mb-4 leading-relaxed">
              It gets harder on three specific counts. Everything — the forms, the online portals, the letters — is
              in German, and the deadlines run from the day a letter is delivered, not the day you understand it.
              The terms have no clean English equivalents, so translating them does not tell you what to do. And the
              expensive mistakes in the first years are not the ones you notice; they are the allowances nobody told
              you about and the deadlines you did not know were running.
            </p>
            <p className="mb-8 leading-relaxed">
              That is the honest case for having someone alongside you — not because the work is impossible, but
              because in year one you do not yet know what you do not know.
            </p>

            {/* pstax-team-group.webp/.jpg (1400×933, "Do you need a tax advisor?"
                section photo) was not supplied in this session — the slot is left
                empty rather than substituting a different photo. See the final
                report for what to add before this page ships. */}

            <PartnerCard />

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_STARTING_OUT.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span
                      aria-hidden="true"
                      className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180 motion-reduce:transition-none"
                    >
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            {/* Official sources */}
            <h2 id="sources" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Official sources
            </h2>
            <p className="mb-4 leading-relaxed">
              The rules on this page come from official Austrian sources. Most of them are in German.
            </p>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                <ExternalLink
                  href="https://www.workinaustria.com/en/residence-employment/residence-permits-for-self-employed/"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Work in Austria — Residence permits for self-employed
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  In English. Which residence titles allow self-employment, and what they require.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/gruendung/gewerbeanmeldung"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Gewerbeanmeldung
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Where to register a trade, which documents you need, free versus regulated trades.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/steuern/neugruendungs-foerderungsgesetz-neugruender"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Neugründungs-Förderungsgesetz (NeuFöG)
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  What the founder exemption covers, the conditions, and the confirmation you need.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Sozialversicherungsanstalt der Selbständigen
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Registration, contribution rules and the insurance threshold for Neue Selbständige.
                </span>
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
                Not sure which route applies to you?
              </h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Book a free 30-minute Tax Check. We work out which registrations you need, in which order, and what
                your first year will cost. You decide afterwards whether you want to work with us. No obligation.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/en/#fit-check" className="btn-accent motion-reduce:transition-none">
                  Book your free Tax Check
                </Link>
                <Link
                  to={SVS_GUIDE_PATH}
                  className="btn-outline !border-white !text-white hover:!bg-white/10 motion-reduce:transition-none"
                >
                  Read the SVS guide
                </Link>
              </div>
              <p className="text-xs text-white/50">
                Fixed annual pricing from €800. 600+ clients in Austria. All correspondence in English
                or German — your choice.
              </p>
            </div>

            {/* Author box */}
            <div className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, Austrian certified tax advisor and founder of PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
                <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">Reviewed by Pascal Schraml</p>
                <p className="text-sm text-pstax-gray mb-3">
                  Austrian certified tax advisor (<span lang="de">Steuerberater</span>), founder of PSTax
                </p>
                <p className="text-sm text-pstax-gray leading-relaxed">
                  Pascal has advised self-employed clients in Austria since 2023 and reviews every guide on this
                  site for technical accuracy. PSTax works with 600+ clients, more than half of
                  them non-native German speakers.
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                <strong className="text-pstax-navy">About this page.</strong> All figures apply to 2026 and change
                annually. This guide gives general information about becoming self-employed in Austria; it is not
                individual advice and cannot replace it. Questions of residence and work authorisation are
                immigration law and outside our professional scope — the sources above are the right starting point
                for those. Reviewed by Pascal Schraml, Austrian certified tax advisor (
                <span lang="de">Steuerberater</span>), PSTax Steuerberatung FlexCo.
              </p>
            </div>
          </article>
      </div>
    </div>
  );
};
